import '../scss/layout/priority-carousel.scss';

export function normalizePriorityItems(items, origin) {
  const seen = new Set();
  return (Array.isArray(items) ? items : []).filter((item) => {
    try {
      const url = new URL(item.url);
      const image = new URL(item.imageUrl);
      if (url.origin !== origin || url.protocol !== 'https:' || url.username || url.password ||
          image.protocol !== 'https:' || image.hostname !== 'storage.ghost.io' || image.username || image.password ||
          typeof item.title !== 'string' || !item.title.trim() || seen.has(url.pathname)) return false;
      seen.add(url.pathname);
      return true;
    } catch { return false; }
  }).slice(0, 4);
}

function element(tag, className, text) {
  const node = document.createElement(tag);
  node.className = className;
  if (text) node.textContent = text;
  return node;
}

function loadImage(image) {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(false), 8000);
    const done = (ok) => { clearTimeout(timeout); resolve(ok); };
    if (image.complete) return done(image.naturalWidth > 0);
    image.onload = () => done(image.naturalWidth > 0);
    image.onerror = () => done(false);
  });
}

export async function initPriorityCarousel() {
  const root = document.querySelector('[data-wla-priority-carousel]');
  if (!root || root.dataset.carouselReady) return;
  // The server-rendered latest article remains available without JS or the feed.
  try {
    const template = root.querySelector('[data-nw-hero-items]');
    if (!template) return;
    const items = normalizePriorityItems([...template.content.querySelectorAll('article')].map(article => ({
      url: article.querySelector('a').href, title: article.querySelector('a').textContent,
      imageUrl: article.querySelector('img')?.src, imageAlt: article.querySelector('img')?.alt || '',
      excerpt: article.querySelector('p').textContent,
      source: article.dataset.featured === 'true' ? 'selected' : 'latest'
    })), window.location.origin);
    if (!items.length) return;

    const carousel = element('section', 'wla-priority-carousel');
    carousel.setAttribute('aria-label', 'Featured stories');
    carousel.setAttribute('aria-roledescription', 'carousel');
    const viewport = element('div', 'wla-priority-carousel__viewport');
    const slides = items.map((item, index) => {
      const slide = element('article', 'wla-priority-carousel__slide');
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `${index + 1} of ${items.length}`);
      slide.hidden = index !== 0;
      const img = element('img', 'wla-priority-carousel__image');
      img.alt = item.imageAlt || '';
      img.decoding = 'async';
      img.src = item.imageUrl;
      const body = element('div', 'wla-priority-carousel__body');
      const label = element('span', 'wla-priority-carousel__label',
        item.source === 'selected' ? 'Editor’s pick' : item.source === 'most-read' ? 'Most read' : 'Latest story');
      const heading = element('h1', 'wla-priority-carousel__title');
      const link = element('a', 'wla-priority-carousel__link', item.title);
      link.href = item.url;
      heading.append(link);
      body.append(label, heading);
      if (item.excerpt) body.append(element('p', 'wla-priority-carousel__excerpt', item.excerpt));
      body.append(element('span', 'wla-priority-carousel__read', 'Read the story →'));
      slide.append(img, body);
      if (item.imageCaption) slide.append(element('small', 'wla-priority-carousel__credit', item.imageCaption));
      viewport.append(slide);
      return slide;
    });
    const loaded = await loadImage(slides[0].querySelector('img'));
    if (!loaded || root.contains(document.activeElement)) return;
    carousel.append(viewport);
    let index = 0;
    let timer;
    let userPaused = false;
    let inView = true;
    let hovering = false;
    let touching = false;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const controls = element('div', 'wla-priority-carousel__controls');
    const status = element('span', 'wla-priority-carousel__status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    const button = (text, label, handler) => {
      const node = element('button', 'wla-priority-carousel__button', text);
      node.type = 'button';
      node.setAttribute('aria-label', label);
      node.addEventListener('click', handler);
      return node;
    };
    const syncTimer = () => {
      clearInterval(timer);
      const running = slides.length > 1 && !userPaused && !reduced.matches && !document.hidden && inView &&
        !hovering && !touching && !carousel.contains(document.activeElement);
      carousel.style.setProperty('--rotation-progress', '0%');
      if (!running) return;
      const deadline = performance.now() + 8000;
      timer = setInterval(() => {
        const remaining = Math.max(0, deadline - performance.now());
        carousel.style.setProperty('--rotation-progress', `${(1 - remaining / 8000) * 100}%`);
        if (remaining === 0) {
          clearInterval(timer);
          show(index + 1);
        }
      }, 100);
    };
    const show = (next, manual = false) => {
      if (touching || settling) resetSwipe();
      const target = (next + slides.length) % slides.length;
      const targetImage = slides[target].querySelector('img');
      if (!targetImage.complete || !targetImage.naturalWidth) { syncTimer(); return; }
      index = target;
      slides.forEach((slide, i) => { slide.hidden = i !== index; });
      dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
      if (manual) {
        userPaused = true;
        status.textContent = `${index + 1} of ${slides.length}: ${items[index].title}`;
        updatePause();
      }
      syncTimer();
    };
    const dots = slides.map((slide, i) => {
      const dot = button(String(i + 1), `Show story ${i + 1}: ${items[i].title}`, () => show(i, true));
      dot.classList.add('wla-priority-carousel__dot');
      dot.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      return dot;
    });
    const pause = button('Pause', 'Pause automatic rotation', () => {
      userPaused = !userPaused;
      updatePause();
      syncTimer();
    });
    pause.classList.add('wla-priority-carousel__pause');
    const updatePause = () => {
      pause.textContent = '';
      pause.dataset.paused = String(userPaused);
      pause.title = userPaused ? 'Start automatic rotation' : 'Pause automatic rotation';
      pause.setAttribute('aria-label', userPaused ? 'Start automatic rotation' : 'Pause automatic rotation');
      pause.hidden = reduced.matches;
    };
    controls.append(...dots, pause);
    controls.hidden = slides.length < 2;
    carousel.append(controls, status);
    root.replaceChildren(carousel);
    root.dataset.carouselReady = 'true';
    carousel.addEventListener('mouseenter', () => { hovering = true; syncTimer(); });
    carousel.addEventListener('mouseleave', () => { hovering = false; syncTimer(); });
    carousel.addEventListener('focusin', (event) => {
      if (event.target !== pause) userPaused = true;
      updatePause(); syncTimer();
    });
    carousel.addEventListener('focusout', () => setTimeout(syncTimer, 0));
    carousel.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      show(index + (event.key === 'ArrowLeft' ? -1 : 1), true);
    });
    let touchStart;
    let suppressClickUntil = 0;
    let swipeTarget = null;
    let settleTimer;
    let settling = false;
    const resetSwipe = () => {
      clearTimeout(settleTimer);
      settling = false;
      swipeTarget = null;
      touchStart = null;
      touching = false;
      viewport.classList.remove('is-swiping');
      slides.forEach((slide, i) => {
        slide.style.transform = '';
        slide.style.transition = '';
        slide.inert = false;
        slide.removeAttribute('aria-hidden');
        slide.hidden = i !== index;
      });
    };
    const renderSwipe = (dx, animate = false) => {
      const width = viewport.clientWidth;
      const direction = dx < 0 ? 1 : -1;
      const target = (index + direction + slides.length) % slides.length;
      const img = slides[target].querySelector('img');
      if (!width || slides.length < 2 || !img.complete || !img.naturalWidth) return false;
      swipeTarget = target;
      viewport.classList.add('is-swiping');
      slides.forEach((slide, i) => {
        slide.hidden = i !== index && i !== target;
        slide.inert = i !== index;
        if (i !== index) slide.setAttribute('aria-hidden', 'true');
        slide.style.transition = animate && !reduced.matches ? 'transform 260ms cubic-bezier(.22,.8,.25,1)' : 'none';
      });
      const offset = Math.max(-width, Math.min(width, dx));
      slides[index].style.transform = `translate3d(${offset}px,0,0)`;
      slides[target].style.transform = `translate3d(${offset + direction * width}px,0,0)`;
      return true;
    };
    const finishSwipe = (dx, commit) => {
      if (swipeTarget === null) { resetSwipe(); syncTimer(); return; }
      const target = swipeTarget;
      const direction = dx < 0 ? 1 : -1;
      settling = true;
      const transition = reduced.matches ? 'none' : 'transform 260ms cubic-bezier(.22,.8,.25,1)';
      slides[index].style.transition = transition;
      slides[target].style.transition = transition;
      slides[index].style.transform = `translate3d(${commit ? -direction * viewport.clientWidth : 0}px,0,0)`;
      slides[target].style.transform = `translate3d(${commit ? 0 : direction * viewport.clientWidth}px,0,0)`;
      settleTimer = setTimeout(() => {
        resetSwipe();
        if (commit) show(target, true);
        else syncTimer();
      }, reduced.matches ? 0 : 270);
    };
    viewport.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'touch' || settling) return;
      if (!event.isPrimary) { resetSwipe(); syncTimer(); return; }
      touchStart = {id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now(), dragging: false, dx: 0};
      touching = true; syncTimer();
    });
    viewport.addEventListener('pointermove', (event) => {
      if (!touchStart || event.pointerId !== touchStart.id) return;
      const dx = event.clientX - touchStart.x, dy = event.clientY - touchStart.y;
      if (!touchStart.dragging && Math.abs(dx) >= 12 && Math.abs(dx) > Math.abs(dy) * 1.25) {
        touchStart.dragging = true;
        viewport.setPointerCapture(event.pointerId);
      }
      if (touchStart.dragging) {
        touchStart.dx = dx;
        renderSwipe(dx);
      }
    });
    viewport.addEventListener('pointerup', (event) => {
      if (!touchStart || event.pointerId !== touchStart.id) return;
      const gesture = touchStart;
      touchStart = null;
      if (!gesture.dragging) { resetSwipe(); syncTimer(); return; }
      suppressClickUntil = Date.now() + 600;
      const dx = gesture.dx;
      const distance = Math.abs(dx);
      const velocity = distance / Math.max(1, performance.now() - gesture.time);
      const commit = distance >= Math.min(100, viewport.clientWidth * .22) || (distance >= 35 && velocity > .5);
      finishSwipe(dx, commit);
    });
    viewport.addEventListener('pointercancel', () => {
      if (touchStart?.dragging) suppressClickUntil = Date.now() + 600;
      resetSwipe(); syncTimer();
    });
    viewport.addEventListener('click', (event) => {
      if (Date.now() < suppressClickUntil) { event.preventDefault(); event.stopPropagation(); }
    }, true);
    document.addEventListener('visibilitychange', syncTimer);
    reduced.addEventListener('change', () => { updatePause(); syncTimer(); });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => { inView = entry.isIntersecting; syncTimer(); }).observe(carousel);
    }
    updatePause();
    syncTimer();
  } catch { /* Keep the server-rendered hero on network/JSON failure. */ }
}
